import React, { useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";
import { AdminLayout } from "../components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { FileDown, Clock, Users, Coffee, TrendingUp, ChevronDown, ChevronUp } from "lucide-react";

const fmt = (mins) => {
  if (!mins) return "0m";
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
};

const fmtDate = (iso) => iso ? new Date(iso).toLocaleString() : "—";

const todayISO = () => new Date().toISOString().slice(0, 10);
const weekISO = () => {
  const now = new Date();
  const d = new Date(now);
  d.setDate(d.getDate() - d.getDay() + (d.getDay() === 0 ? -6 : 1));
  const year = d.getFullYear();
  const startOfYear = new Date(year, 0, 1);
  const week = Math.ceil(((d - startOfYear) / 86400000 + startOfYear.getDay() + 1) / 7);
  return `${year}-W${String(week).padStart(2, "0")}`;
};
const monthISO = () => new Date().toISOString().slice(0, 7);

export default function AdminAttendance() {
  const [data, setData] = useState({ logs: [], summary: [] });
  const [users, setUsers] = useState([]);
  const [period, setPeriod] = useState("monthly");
  const [date, setDate] = useState(monthISO());
  const [filterUser, setFilterUser] = useState("all");
  const [expandedUser, setExpandedUser] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [tsDays, setTsDays] = useState("30");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ period, date });
      if (filterUser !== "all") params.set("user_id", filterUser);
      const [att, u] = await Promise.all([
        api.get(`/admin/attendance?${params}`),
        api.get("/admin/users"),
      ]);
      setData(att.data);
      setUsers(u.data.filter((u) => u.role === "worker" || u.role === "manager"));
    } catch { toast.error("Failed to load attendance"); }
    finally { setLoading(false); }
  }, [period, date, filterUser]);

  useEffect(() => { load(); }, [load]);

  const handlePeriodChange = (val) => {
    setPeriod(val);
    if (val === "daily") setDate(todayISO());
    else if (val === "weekly") setDate(weekISO());
    else if (val === "monthly") setDate(monthISO());
    else setDate("");
  };

  const forceOut = async (id) => {
    try { await api.post(`/admin/attendance/${id}/force-punch-out`); toast.success("Force punched out"); load(); }
    catch { toast.error("Failed"); }
  };

  const downloadPdf = async () => {
    setDownloading(true);
    try {
      const r = await api.get(`/admin/reports/timesheet?days=${tsDays}`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `timesheet_${new Date().toISOString().slice(0, 10)}_${tsDays}d.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Timesheet downloaded");
    } catch { toast.error("Download failed"); }
    finally { setDownloading(false); }
  };

  const totalMinutes = data.summary.reduce((s, u) => s + u.total_minutes, 0);
  const totalNet = data.summary.reduce((s, u) => s + u.net_minutes, 0);
  const totalBreak = data.summary.reduce((s, u) => s + u.total_break_minutes, 0);

  // filter raw logs by selected user for the detail table
  const visibleLogs = filterUser !== "all"
    ? data.logs.filter((l) => l.user_id === filterUser)
    : data.logs;

  return (
    <AdminLayout>
      {/* Header */}
      <div className="flex items-end justify-between mb-6 flex-wrap gap-4">
        <div>
          <div className="label-overline text-[#D4AF37]">Work</div>
          <h1 className="font-display text-4xl mt-2">Attendance</h1>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Select value={tsDays} onValueChange={setTsDays}>
            <SelectTrigger className="bg-[#132018] border-[#21362A] w-36 h-9"><SelectValue/></SelectTrigger>
            <SelectContent className="bg-[#0C140F] border-[#21362A] text-white">
              {["7","14","30","60","90"].map((d) => <SelectItem key={d} value={d}>{`PDF: Last ${d}d`}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button onClick={downloadPdf} disabled={downloading} className="bg-[#D4AF37] hover:bg-[#F0C84A] text-[#050A07]" data-testid="download-timesheet-btn">
            <FileDown className="w-4 h-4 mr-2"/>{downloading ? "Generating…" : "Download PDF"}
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 mb-6">
        {/* Period */}
        <Select value={period} onValueChange={handlePeriodChange}>
          <SelectTrigger className="bg-[#132018] border-[#21362A] w-36 h-9"><SelectValue/></SelectTrigger>
          <SelectContent className="bg-[#0C140F] border-[#21362A] text-white">
            <SelectItem value="all">All time</SelectItem>
            <SelectItem value="daily">Daily</SelectItem>
            <SelectItem value="weekly">Weekly</SelectItem>
            <SelectItem value="monthly">Monthly</SelectItem>
          </SelectContent>
        </Select>

        {/* Date picker depends on period */}
        {period === "daily" && (
          <input type="date" value={date} onChange={(e) => setDate(e.target.value)}
            className="bg-[#132018] border border-[#21362A] text-white text-sm rounded-sm px-3 h-9 focus:outline-none focus:border-[#D4AF37]" />
        )}
        {period === "weekly" && (
          <input type="week" value={date} onChange={(e) => setDate(e.target.value)}
            className="bg-[#132018] border border-[#21362A] text-white text-sm rounded-sm px-3 h-9 focus:outline-none focus:border-[#D4AF37]" />
        )}
        {period === "monthly" && (
          <input type="month" value={date} onChange={(e) => setDate(e.target.value)}
            className="bg-[#132018] border border-[#21362A] text-white text-sm rounded-sm px-3 h-9 focus:outline-none focus:border-[#D4AF37]" />
        )}

        {/* User filter */}
        <Select value={filterUser} onValueChange={setFilterUser}>
          <SelectTrigger className="bg-[#132018] border-[#21362A] w-48 h-9"><SelectValue placeholder="All workers"/></SelectTrigger>
          <SelectContent className="bg-[#0C140F] border-[#21362A] text-white">
            <SelectItem value="all">All workers</SelectItem>
            {users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name || u.email}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {/* Aggregate stat cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <Card className="bg-[#0C140F] border-[#21362A] rounded-sm">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-[#D4AF37]/10 flex items-center justify-center shrink-0">
              <Users className="w-4 h-4 text-[#D4AF37]"/>
            </div>
            <div>
              <div className="text-xs text-[#A1B4A8] uppercase tracking-widest">Workers</div>
              <div className="font-display text-2xl text-white">{data.summary.length}</div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-[#0C140F] border-[#21362A] rounded-sm">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-[#2A9D8F]/10 flex items-center justify-center shrink-0">
              <Clock className="w-4 h-4 text-[#2A9D8F]"/>
            </div>
            <div>
              <div className="text-xs text-[#A1B4A8] uppercase tracking-widest">Total Clocked</div>
              <div className="font-display text-2xl text-white">{fmt(totalMinutes)}</div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-[#0C140F] border-[#21362A] rounded-sm">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-[#097969]/10 flex items-center justify-center shrink-0">
              <TrendingUp className="w-4 h-4 text-[#097969]"/>
            </div>
            <div>
              <div className="text-xs text-[#A1B4A8] uppercase tracking-widest">Net Work</div>
              <div className="font-display text-2xl text-white">{fmt(totalNet)}</div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-[#0C140F] border-[#21362A] rounded-sm">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-[#E63946]/10 flex items-center justify-center shrink-0">
              <Coffee className="w-4 h-4 text-[#E63946]"/>
            </div>
            <div>
              <div className="text-xs text-[#A1B4A8] uppercase tracking-widest">Total Break</div>
              <div className="font-display text-2xl text-white">{fmt(totalBreak)}</div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Per-user summary table */}
      <Card className="bg-[#0C140F] border-[#21362A] rounded-sm mb-6">
        <CardHeader className="pb-2 pt-4 px-4">
          <CardTitle className="text-base font-semibold text-[#F5F5F5]">Worker Summary</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <table className="w-full text-sm">
            <thead className="text-[#A1B4A8] text-xs uppercase tracking-widest border-b border-[#21362A]">
              <tr>
                <th className="text-left p-4">Worker</th>
                <th className="text-right p-4">Shifts</th>
                <th className="text-right p-4">Total Clocked</th>
                <th className="text-right p-4">Break</th>
                <th className="text-right p-4">Net Work</th>
                <th className="p-4"></th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={6} className="text-center p-8 text-[#A1B4A8]">Loading…</td></tr>
              )}
              {!loading && data.summary.length === 0 && (
                <tr><td colSpan={6} className="text-center p-8 text-[#A1B4A8]">No attendance records for this period.</td></tr>
              )}
              {!loading && data.summary.map((s) => (
                <React.Fragment key={s.user_id}>
                  <tr
                    className="border-t border-[#21362A] cursor-pointer hover:bg-[#132018]/60 transition-colors"
                    onClick={() => setExpandedUser(expandedUser === s.user_id ? null : s.user_id)}
                  >
                    <td className="p-4 font-medium">{s.user_name}</td>
                    <td className="text-right p-4 text-[#A1B4A8]">{s.shifts}</td>
                    <td className="text-right p-4">
                      <span className="text-[#2A9D8F] font-semibold">{fmt(s.total_minutes)}</span>
                      <span className="text-[#A1B4A8] text-xs ml-1">({s.total_hours}h)</span>
                    </td>
                    <td className="text-right p-4 text-[#E63946]">{fmt(s.total_break_minutes)}</td>
                    <td className="text-right p-4">
                      <span className="text-[#D4AF37] font-semibold">{fmt(s.net_minutes)}</span>
                      <span className="text-[#A1B4A8] text-xs ml-1">({s.net_hours}h)</span>
                    </td>
                    <td className="p-4 text-right text-[#A1B4A8]">
                      {expandedUser === s.user_id ? <ChevronUp className="w-4 h-4 inline"/> : <ChevronDown className="w-4 h-4 inline"/>}
                    </td>
                  </tr>
                  {/* Inline detail rows for this user */}
                  {expandedUser === s.user_id && data.logs.filter((l) => l.user_id === s.user_id).map((a) => (
                    <tr key={a.id} className="bg-[#132018]/40 border-t border-[#21362A]/50">
                      <td className="pl-8 py-2 text-[#A1B4A8] text-xs">↳ {fmtDate(a.punch_in)}</td>
                      <td className="text-right py-2 text-xs text-[#A1B4A8]">1</td>
                      <td className="text-right py-2 text-xs">
                        {fmt(a.total_minutes || 0)}
                        <span className="text-[#A1B4A8] ml-1">→ {fmtDate(a.punch_out)}</span>
                      </td>
                      <td className="text-right py-2 text-xs text-[#E63946]">{fmt(a.break_minutes || 0)}</td>
                      <td className="text-right py-2 text-xs text-[#D4AF37]">{fmt(Math.max(0, (a.total_minutes || 0) - (a.break_minutes || 0)))}</td>
                      <td className="py-2 pr-4 text-right">
                        {a.punch_out
                          ? (a.auto_punched_out
                            ? <Badge className="bg-[#E63946]/15 text-[#E63946] border border-[#E63946]/30 text-xs">auto</Badge>
                            : <Badge className="bg-[#132018] border border-[#21362A] text-[#A1B4A8] text-xs">closed</Badge>)
                          : <Badge className="bg-[#097969]/20 text-[#2A9D8F] border border-[#097969]/40 text-xs">open</Badge>
                        }
                        {!a.punch_out && (
                          <Button size="sm" variant="outline" className="border-[#E63946] text-[#E63946] ml-2 h-6 text-xs px-2"
                            onClick={(e) => { e.stopPropagation(); forceOut(a.id); }}
                            data-testid={`force-out-${a.id}`}>Force out</Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {/* Raw log table (collapsed view) */}
      <Card className="bg-[#0C140F] border-[#21362A] rounded-sm">
        <CardHeader className="pb-2 pt-4 px-4">
          <CardTitle className="text-base font-semibold text-[#F5F5F5]">
            All Sessions
            <span className="text-[#A1B4A8] text-xs font-normal ml-2">({visibleLogs.length} records)</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0 overflow-x-auto">
          <table className="w-full text-sm" data-testid="attendance-table">
            <thead className="text-[#A1B4A8] text-xs uppercase tracking-widest border-b border-[#21362A]">
              <tr>
                <th className="text-left p-4">Worker</th>
                <th className="text-left p-4">Punch In</th>
                <th className="text-left p-4">Punch Out</th>
                <th className="text-right p-4">Clocked</th>
                <th className="text-right p-4">Break</th>
                <th className="text-right p-4">Net</th>
                <th className="text-left p-4">Status</th>
                <th className="p-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {visibleLogs.length === 0 && !loading && (
                <tr><td colSpan={8} className="text-center p-8 text-[#A1B4A8]">No sessions.</td></tr>
              )}
              {visibleLogs.map((a) => {
                const net = Math.max(0, (a.total_minutes || 0) - (a.break_minutes || 0));
                return (
                  <tr key={a.id} className="border-t border-[#21362A] hover:bg-[#132018]/40 transition-colors">
                    <td className="p-4 font-medium">{a.user_name}</td>
                    <td className="p-4 text-[#A1B4A8] whitespace-nowrap">{fmtDate(a.punch_in)}</td>
                    <td className="p-4 text-[#A1B4A8] whitespace-nowrap">{fmtDate(a.punch_out)}</td>
                    <td className="text-right p-4 text-[#2A9D8F]">{fmt(a.total_minutes || 0)}</td>
                    <td className="text-right p-4 text-[#E63946]">{fmt(a.break_minutes || 0)}</td>
                    <td className="text-right p-4 text-[#D4AF37] font-semibold">{fmt(net)}</td>
                    <td className="p-4">
                      {a.punch_out
                        ? (a.auto_punched_out
                          ? <Badge className="bg-[#E63946]/15 text-[#E63946] border border-[#E63946]/30">auto</Badge>
                          : <Badge className="bg-[#132018] border border-[#21362A] text-[#A1B4A8]">closed</Badge>)
                        : <Badge className="bg-[#097969]/20 text-[#2A9D8F] border border-[#097969]/40">open</Badge>
                      }
                    </td>
                    <td className="p-4 text-right">
                      {!a.punch_out && (
                        <Button size="sm" variant="outline" className="border-[#E63946] text-[#E63946]"
                          onClick={() => forceOut(a.id)} data-testid={`force-out-${a.id}`}>Force out</Button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </AdminLayout>
  );
}
